import { useEffect, useRef, useState } from 'react'
import { formatPlaybackRate, PRACTICE_PLAYBACK_RATES, type PracticePlaybackRate } from '../lib/playbackRate'
import { useI18n } from '../i18n/useI18n'
import './PlaybackSpeedControl.css'

interface Props {
  value: PracticePlaybackRate
  onChange: (rate: PracticePlaybackRate) => void
  disabled?: boolean
  compact?: boolean
  className?: string
}

export function PlaybackSpeedControl({ value, onChange, disabled = false, compact = false, className = '' }: Props) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const label = formatPlaybackRate(value)

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  return (
    <div
      ref={rootRef}
      className={`playback-speed-control ${compact ? 'compact' : ''} ${className}`.trim()}
    >
      <button
        type="button"
        className="button ghost playback-speed-trigger"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('playback.speedAria', { rate: label })}
        title={t('playback.speed')}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
      </button>
      {open && (
        <div className="card playback-speed-menu" role="menu" aria-label={t('playback.speed')}>
          {PRACTICE_PLAYBACK_RATES.map((rate) => {
            const optionLabel = formatPlaybackRate(rate)
            return (
              <button
                type="button"
                role="menuitemradio"
                aria-checked={rate === value}
                aria-label={t('playback.speedOption', { rate: optionLabel })}
                className={rate === value ? 'button ghost playback-speed-option active' : 'button ghost playback-speed-option'}
                key={rate}
                onClick={() => {
                  onChange(rate)
                  setOpen(false)
                }}
              >
                <span>{optionLabel}</span>
                {rate === value && <span className="playback-speed-check" aria-hidden="true">✓</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
